// src/hooks/useComplianceManager.ts
import { useState, useEffect, useCallback } from 'react';
import { Incident, ComplianceTask, IncidentStatus, ActionStatus, Resident } from '../types/compliance';

export function useComplianceManager() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [tasks, setTasks] = useState<ComplianceTask[]>([]);
  const [residents, setResidents] = useState<Resident[]>([]);

  useEffect(() => {
    const storedIncidents = localStorage.getItem('rmacs_compliance_incidents');
    if (storedIncidents) {
      try { setIncidents(JSON.parse(storedIncidents)); } catch {}
    }
    const storedTasks = localStorage.getItem('rmacs_compliance_tasks');
    if (storedTasks) {
      try { setTasks(JSON.parse(storedTasks)); } catch {}
    }
    const storedResidents = localStorage.getItem('rmacs_compliance_residents');
    if (storedResidents) {
      try { setResidents(JSON.parse(storedResidents)); } catch {}
    }
  }, []);

  const saveIncidents = (data: Incident[]) => {
    localStorage.setItem('rmacs_compliance_incidents', JSON.stringify(data));
    setIncidents(data);
  };
  const saveTasks = (data: ComplianceTask[]) => {
    localStorage.setItem('rmacs_compliance_tasks', JSON.stringify(data));
    setTasks(data);
  };
  const saveResidents = (data: Resident[]) => {
    localStorage.setItem('rmacs_compliance_residents', JSON.stringify(data));
    setResidents(data);
  };

  const closeIncidentLoop = useCallback(async (
    incidentId: string,
    managerSignature: string,
    analysis: string,
    outcomes: string
  ) => {
    const updatedIncidents = incidents.map(inc => {
      if (inc.id === incidentId) {
        return {
          ...inc,
          status: IncidentStatus.CLOSED,
          rootCauseAnalysis: analysis,
          lessonsLearned: outcomes,
          closedAt: new Date().toISOString(),
          closedByManagerSignature: managerSignature,
        };
      }
      return inc;
    });
    saveIncidents(updatedIncidents);
  }, [incidents]);

  const checkAndUpdateOverdueTasks = useCallback(() => {
    const now = new Date();
    let updated = false;
    const newTasks = tasks.map(task => {
      if (
        (task.status === ActionStatus.PENDING || task.status === ActionStatus.IN_PROGRESS) &&
        new Date(task.dueDate) < now
      ) {
        updated = true;
        return { ...task, status: ActionStatus.OVERDUE };
      }
      return task;
    });
    if (updated) {
      saveTasks(newTasks);
    }
  }, [tasks]);

  useEffect(() => {
    const interval = setInterval(checkAndUpdateOverdueTasks, 60000);
    return () => clearInterval(interval);
  }, [checkAndUpdateOverdueTasks]);

  useEffect(() => {
    checkAndUpdateOverdueTasks();
  }, [checkAndUpdateOverdueTasks]);

  return {
    incidents,
    tasks,
    residents,
    closeIncidentLoop,
    checkAndUpdateOverdueTasks,
    saveIncidents,
    saveTasks,
    saveResidents,
  };
}